/* ==========================================================================
   Oma Nimitarra product designer, version 2
   --------------------------------------------------------------------------
   Drives the product page: live sticker preview, a paged grid of ready-made
   designs (colour, pattern, art), fonts, art picker, fine tuning of colours,
   optional photo upload, validation and add-to-cart.

   What gets sent to the cart (line item properties):
     Nimi, Toinen rivi, Kolmas rivi, Malli, Fontti, Koriste, Isot kirjaimet
                             human readable, shown in cart, checkout and the order
     Tarran väri, Tekstin väri, Kuvio
                             only when the design was fine-tuned
     _design                 hidden JSON for the print pipeline (schema in sticker.js)
     Kuva                    uploaded picture (photo products only), stored by Shopify

   Depends on window.OmaSticker (sticker.js, sticker-designs.js, sticker-art-core.js),
   window.NLUtil, window.NLCart and window.NL (theme.js / js-globals.liquid).
   ========================================================================== */
(function () {
  'use strict';

  var S = window.OmaSticker;
  var U = window.NLUtil;
  var NL = window.NL || { strings: {}, currency: 'EUR' };
  var STORE_KEY = 'nl.design.v2';
  var MAX_FILE = 10 * 1024 * 1024;
  var PHOTO_MAX_SIDE = 2000;                    // longest side (px) of a photo after the browser shrinks it for upload
  var PHOTO_SHRINK_ABOVE = 2.5 * 1024 * 1024;   // a picture within PHOTO_MAX_SIDE and under this size is uploaded as it is
  var PHOTO_SHEET_SIDE = 640;                   // longest side (px) of the small copy the sheet preview draws on every label
  var PHOTO_MIN_PPI = 150;                      // below this the printed picture gets soft (print files are resampled to 300 ppi at most)
  var DEFAULT_DESIGN = 'hai';
  var PLAIN_DESIGN = 'valkoinen';               // "Pelkkä teksti": the neutral white label without art or pattern

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function esc(s) { return U.escapeHtml(s == null ? '' : s); }
  function round(n) { return Math.round((n || 0) * 100) / 100; }

  function tpl(str, vars) {
    return String(str || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, k) { return vars[k] != null ? vars[k] : ''; });
  }

  /* ---------- Shape spec from the product / variant configuration --------------------- */

  function parseDims(title) {
    // "22 x 12 mm", "15,5x5,5mm", "18 mm"
    var t = String(title || '').replace(',', '.').toLowerCase();
    var m = t.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/);
    if (m) return { w: parseFloat(m[1]), h: parseFloat(m[2]), rect: true };
    m = t.match(/(\d+(?:\.\d+)?)\s*mm/);
    if (m) return { w: parseFloat(m[1]), h: parseFloat(m[1]), rect: false };
    return null;
  }

  function resolveSpec(config, variantTitle) {
    var c = config.cfg || {};
    var v = c.variants ? (c.variants[variantTitle] || null) : null;
    var base = {};
    Object.keys(c).forEach(function (k) { if (k !== 'variants') base[k] = c[k]; });
    if (v) Object.keys(v).forEach(function (k) { base[k] = v[k]; });

    var spec = {
      shape: base.shape || 'round',
      w: base.w, h: base.h,
      perSheet: base.perSheet, format: base.format || 'A5',
      scene: base.scene || 'school'
    };
    if (!spec.w && !v) {
      var d = parseDims(variantTitle);
      if (d) {
        spec.w = d.w; spec.h = d.h;
        if (!base.shape) spec.shape = d.rect ? (d.w === d.h ? 'square' : (d.h < 8 ? 'mini' : (d.w / d.h > 3 ? 'wide' : 'oblong'))) : 'round';
      }
    }
    var def = S.data.SHAPES[spec.shape === 'mixed' ? 'round' : spec.shape] || S.data.SHAPES.round;
    if (!spec.w) { spec.w = def.w; spec.h = def.h; }

    // Kits (paketit): several sheets that share one name and one design.
    if (Array.isArray(c.items) && c.items.length) {
      spec.items = c.items.map(function (it) {
        var d = S.data.SHAPES[it.shape === 'mixed' ? 'round' : it.shape] || S.data.SHAPES.round;
        return { key: it.key, label: it.label, shape: it.shape, w: it.w || d.w, h: it.h || d.h, perSheet: it.perSheet || null, format: it.format || 'A5', qty: it.qty || 1 };
      });
      var first = spec.items[0];
      spec.shape = first.shape; spec.w = first.w; spec.h = first.h; spec.perSheet = null; spec.format = first.format;
      if (first.scene) spec.scene = first.scene;
    }
    return spec;
  }

  /* ---------- Designer --------------------------------------------------------------------- */

  function Designer(root) {
    this.root = root;
    var cfgEl = $('[data-designer-config]', root);
    this.config = cfgEl ? JSON.parse(cfgEl.textContent) : { cfg: {}, variants: [], mode: 'name', maxChars: 20 };
    this.form = $('[data-designer-form]', root);
    if (!this.form) return;

    this.el = {
      single: $('[data-stage-single]', root),
      use: $('[data-stage-use]', root),
      sheet: $('[data-stage-sheet]', root),
      actual: $('[data-stage-actual]', root),
      count: $('[data-stage-count]', root),
      size: $('[data-stage-size]', root),
      mini: $('[data-stage-mini]', root),
      warnings: $('[data-warnings]', root),
      text: $('[data-field="text"]', root),
      text2: $('[data-field="text2"]', root),
      text2Wrap: $('[data-line2-wrap]', root),
      text3: $('[data-field="text3"]', root),
      text3Wrap: $('[data-line3-wrap]', root),
      upper: $('[data-field="upper"]', root),
      plain: $('[data-field="plain"]', root),
      counter: $('[data-counter]', root),
      nameError: $('[data-name-error]', root),
      props: $('[data-props]', root),
      variantId: $('[data-variant-id]', root),
      qty: $('[data-designer-qty]', root),
      total: $('[data-total-price]', root),
      unit: $('[data-unit-price]', root),
      submit: $('[data-add-to-cart]', root),
      submitLabel: $('[data-submit-label]', root),
      formError: $('[data-form-error]', root),
      fitNote: $('[data-fit-note]', root),
      designName: $('[data-design-name]', root),
      artName: $('[data-art-name]', root),
      customName: $('[data-custom-name]', root),
      colorName: $('[data-color-name]', root),
      textColorNames: $$('[data-text-color-name]', root),
      fontName: $('[data-font-name]', root),
      variantName: $('[data-variant-name]', root),
      resetDesign: $('[data-reset-design]', root),
      photoInput: $('[data-photo-input]', root),
      photoTools: $('[data-photo-tools]', root),
      photoTitle: $('[data-photo-title]', root)
    };

    this.mode = this.config.mode || 'name';
    this.maxChars = this.config.maxChars || 20;
    if (this.mode === 'photo' && this.el.text) this.el.text.required = false;
    this.variantId = this.config.currentVariant;
    this.variant = this.currentVariant();
    this.spec = resolveSpec(this.config, this.variant && this.variant.title);

    this.state = {
      text: '', text2: '', text3: '', upper: false, font: 'blocky',
      fontCustom: false,
      design: null, bg: null, fg: null, pattern: null, art: null, custom: false, fgCustom: false,
      lookBefore: null,   // the look that "Pelkkä teksti" replaced, so unticking brings it back
      tab: 'all', page: 0, q: '', artGroup: 'elaimet',
      qty: 1, image: null, view: 'single'
    };
    this.sample = 'Aino';

    // The product can open on a design that suits its buyers (metafield `defaultDesign`, e.g. a calm one for the care home kit)
    var dd = this.config.cfg && this.config.cfg.defaultDesign;
    this.applyDesign(dd && S.designById(dd) ? dd : DEFAULT_DESIGN);
    // Photo stickers: a neutral placeholder until a picture is chosen, then white text with a halo
    if (this.mode === 'photo') { this.state.bg = '#DCEBFA'; this.state.fg = '#1B394E'; this.state.art = null; this.state.pattern = null; }
    this.restore();
    this.build();
    this.bind();
    this.initLibrary();
    this.applySpec();
    this.paint();
    this.watchStage();
  }

  // Phones: the big preview scrolls away with the page. Once it is out of sight a slim fixed bar under the header shows
  // the sticker, so the result stays in view while the form is filled in. The bar is out of the page flow, so showing and
  // hiding it moves nothing (an earlier version shrank a sticky preview, which shifted the content and flickered).
  Designer.prototype.watchStage = function () {
    var stage = $('[data-stage]', this.root), mini = this.el.mini;
    if (!stage || !mini || !('IntersectionObserver' in window)) return;
    var mq = window.matchMedia('(max-width: 989px)');
    var io = new IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      var edge = e.rootBounds ? e.rootBounds.top : 70;
      // gone = the whole preview is above the area under the header
      var gone = !e.isIntersecting && e.boundingClientRect.bottom <= edge;
      mini.classList.toggle('is-visible', gone && mq.matches);
    }, { threshold: 0, rootMargin: '-70px 0px 0px 0px' });
    io.observe(stage);
    var leaveMobile = function () { if (!mq.matches) mini.classList.remove('is-visible'); };
    if (mq.addEventListener) mq.addEventListener('change', leaveMobile); else if (mq.addListener) mq.addListener(leaveMobile);
  };

  Designer.prototype.currentVariant = function () {
    var id = this.variantId;
    var list = this.config.variants || [];
    for (var i = 0; i < list.length; i++) if (String(list[i].id) === String(id)) return list[i];
    return list[0];
  };

  /* ---------- Design state --------------------------------------------------------------------- */

  // The font a design looks best in (work labels: condensed, classic ones: serif). Used until the visitor picks a font themselves.
  Designer.prototype.designFont = function (id) {
    var d = S.designById(id);
    return d && d.f && S.byId(S.data.FONTS, d.f) ? d.f : 'blocky';
  };

  // Takes over everything a design defines: colours, pattern, art. Fine-tuning is reset.
  Designer.prototype.applyDesign = function (id) {
    var st = this.state;
    var d = S.designById(id) || (S.designs && S.designs[0]);
    st.custom = false; st.fgCustom = false;
    if (!d) {
      st.design = null; st.bg = '#66C7E9'; st.fg = '#1B394E'; st.pattern = null; st.art = null;
      return;
    }
    var t = S.toneOf(d, 0);
    st.design = d.id;
    st.bg = t.bg; st.fg = t.fg;
    st.pattern = S.patternOf(d);
    st.art = d.c ? null : (d.a || null);
  };

  // "Pelkkä teksti, ei koristetta": a look with no art and no pattern (a frame counts as decoration). The tick box shows what the
  // look is, so it follows when a design, an icon or a pattern is picked further down. Ticking it takes the neutral white design
  // and remembers the look it replaced; unticking brings that look back, or the opening design when there is nothing to bring back.
  Designer.prototype.isPlain = function () {
    var st = this.state;
    return !st.image && !st.art && !st.pattern;
  };

  Designer.prototype.setPlain = function (on) {
    var st = this.state;
    if (this.mode === 'photo' || on === this.isPlain()) { this.syncPickers(); return; }
    if (on) {
      st.lookBefore = { design: st.design, custom: st.custom, fgCustom: st.fgCustom, bg: st.bg, fg: st.fg, pattern: st.pattern, art: st.art };
      this.applyDesign(PLAIN_DESIGN);
    } else {
      var b = st.lookBefore;
      if (b && (b.art || b.pattern)) {
        st.design = b.design; st.custom = b.custom; st.fgCustom = b.fgCustom; st.bg = b.bg; st.fg = b.fg; st.pattern = b.pattern; st.art = b.art;
      } else {
        this.applyDesign(DEFAULT_DESIGN);
      }
      st.lookBefore = null;
    }
    if (!st.fontCustom) st.font = this.designFont(st.design);
    this.gotoDesign(st.design);
    this.syncPickers();
    this.schedulePaint();
  };

  Designer.prototype.palette = function () {
    var st = this.state;
    var d = st.design && S.designById(st.design);
    return d && d.c && d.c.length > 1 && !st.custom ? d.c : null;
  };

  // The look of the n-th tone of the current design (0 = the design itself), as explicit render options.
  Designer.prototype.toneStyle = function (k) {
    var st = this.state;
    if (!k) return { bg: st.bg, fg: st.fg, pattern: st.pattern, art: st.art };
    // Patterns stay on the main label only; a frame is part of the style, so the other labels keep it
    var frame = st.pattern && /^frame/.test(st.pattern.id) ? st.pattern.id : null;
    var d = st.design && S.designById(st.design);
    if (d && !st.custom) {
      var t = S.toneOf(d, k);
      return { bg: t.bg, fg: t.fg, pattern: frame, art: st.art };
    }
    var light = S.mix(st.bg, '#FFFFFF', 0.5), dark = S.mix(st.bg, '#000000', 0.26);
    var bg = k === 1 ? light : dark, fg = S.bestFg(bg);
    // A text colour the visitor picked belongs to the whole sheet (Vate 19.10.2026: on a mixed sheet the long labels kept black or white text).
    // If it does not read on the usual tone (white text on a lighter tone), the label takes the other tone instead (a darker one), so the
    // picked colour stays; only when it reads on neither does the label fall back to black or white text.
    if (st.fgCustom) {
      if (S.contrast(bg, st.fg) >= 3) fg = st.fg;
      else if (S.contrast(k === 1 ? dark : light, st.fg) >= 3) { bg = k === 1 ? dark : light; fg = st.fg; }
    }
    return { bg: bg, fg: fg, pattern: frame, art: st.art };
  };

  // Tone styles a sheet can use, and how it uses them
  Designer.prototype.styles = function () {
    var pal = this.palette(), out = [], n = pal ? Math.min(pal.length, 6) : 3;
    for (var i = 0; i < n; i++) out.push(this.toneStyle(i));
    return out;
  };
  Designer.prototype.mixFor = function (g) {
    if (g.shape === 'mixed') return this.palette() ? 'cols' : 'shapes';
    return this.palette() ? 'rows' : '';
  };

  /* Remember the design between products (sessionStorage) and read ?name= ?design= ?font= from the URL. */
  Designer.prototype.restore = function () {
    var st = this.state, saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) {}
    if (saved) {
      ['text', 'text2', 'text3', 'upper', 'font', 'fontCustom'].forEach(function (k) {
        if (saved[k] !== undefined && saved[k] !== null) st[k] = saved[k];
      });
      if (saved.design && S.designById(saved.design) && this.mode !== 'photo') {
        this.applyDesign(saved.design);
        if (saved.custom) {
          st.custom = true; st.fgCustom = !!saved.fgCustom;
          if (saved.bg) st.bg = saved.bg;
          if (saved.fg) st.fg = saved.fg;
          st.pattern = saved.pattern || null;
        }
        if (saved.art !== undefined) st.art = saved.art;
      }
      if (this.mode === 'photo' && saved.fgCustom && saved.fg) { st.fg = saved.fg; st.fgCustom = true; }
    }
    var q = new URLSearchParams(window.location.search);
    if (q.get('name')) st.text = q.get('name').slice(0, this.maxChars);
    var qd = q.get('design') || q.get('color');
    if (qd && S.designById(qd) && this.mode !== 'photo') { this.applyDesign(qd); if (!st.fontCustom) st.font = this.designFont(qd); }
    var qf = q.get('font');
    if (qf && S.byId(S.data.FONTS, qf)) st.font = qf;
    st.font = S.fontOf(st.font).id;
    if (st.art && !S.art[st.art] && S.artIndex[st.art]) this.needArt = true;
    if (st.art && !S.artIndex[st.art]) st.art = null;
    // The grid opens on the page of the current design
    this.gotoDesign(st.design, true);
  };

  Designer.prototype.save = function () {
    var st = this.state;
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        text: st.text, text2: st.text2, text3: st.text3, upper: st.upper, font: st.font, fontCustom: st.fontCustom,
        design: st.design, custom: st.custom, fgCustom: st.fgCustom, bg: st.bg, fg: st.fg, pattern: st.pattern, art: st.art
      }));
    } catch (e) {}
  };

  /* ---------- Build the pickers ---------------------------------------------------------------- */

  Designer.prototype.pageSize = function () {
    return window.matchMedia('(max-width: 519px)').matches ? 6 : 8;
  };

  Designer.prototype.designsOf = function (tab) {
    var list = S.designs || [];
    var q = this.state.q;
    if (q) {
      return list.filter(function (d) { return d.n.toLowerCase().indexOf(q) > -1 || d.id.indexOf(q) > -1; });
    }
    return tab === 'all' ? list : list.filter(function (d) { return d.g === tab; });
  };

  // Points the grid at the page that holds a design (in the "all" tab)
  Designer.prototype.gotoDesign = function (id, quiet) {
    var st = this.state;
    var list = this.designsOf(st.tab), i = -1;
    for (var k = 0; k < list.length; k++) if (list[k].id === id) { i = k; break; }
    if (i < 0) { st.tab = 'all'; list = this.designsOf('all'); for (k = 0; k < list.length; k++) if (list[k].id === id) { i = k; break; } }
    if (i >= 0) st.page = Math.floor(i / this.pageSize());
    if (!quiet) this.renderGrid();
  };

  Designer.prototype.build = function () {
    var self = this;
    var root = this.root;

    function swatches(kind, list) {
      var boxes = $$('[data-swatches="' + kind + '"]', root);
      boxes.forEach(function (box) {
        box.innerHTML = '';
        list.forEach(function (c) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'swatch swatch--lg';
          b.setAttribute('role', 'radio');
          b.dataset.key = kind;
          b.dataset.value = c.id;
          b.style.setProperty('--swatch', c.hex);
          b.title = c.name;
          b.setAttribute('aria-label', c.name);
          b.innerHTML = '<svg viewBox="0 0 256 256" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M229.66 77.66l-128 128a8 8 0 0 1-11.32 0l-56-56a8 8 0 0 1 11.32-11.32L96 188.69 218.34 66.34a8 8 0 0 1 11.32 11.32Z"/></svg>';
          b.style.color = S.contrast(c.hex, '#FFFFFF') > S.contrast(c.hex, '#1D1D1B') ? '#fff' : '#1D1D1B';
          box.appendChild(b);
        });
      });
    }
    swatches('bg', S.data.COLORS);
    swatches('fg', S.data.TEXT_COLORS);

    var fonts = $('[data-fonts]', root);
    if (fonts) {
      fonts.innerHTML = '';
      S.data.FONTS.forEach(function (f) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'font-card';
        b.setAttribute('role', 'radio');
        b.dataset.key = 'font';
        b.dataset.value = f.id;
        b.innerHTML = '<span class="font-card__sample" data-ff="' + esc(f.family) + '" data-fw="' + f.weight + '" style="font-size:' + round(1.32 * (f.k || 1)) + 'rem"></span><span class="font-card__name">' + f.name + '</span>';
        fonts.appendChild(b);
      });
      // The nine fonts are only fetched when the font step is about to come into view
      var applied = false;
      var applyFonts = function () {
        if (applied) return;
        applied = true;
        $$('.font-card__sample', fonts).forEach(function (el) { el.style.fontFamily = el.dataset.ff; el.style.fontWeight = el.dataset.fw; });
      };
      if ('IntersectionObserver' in window) {
        var fio = new IntersectionObserver(function (entries) { if (entries[0].isIntersecting) { applyFonts(); fio.disconnect(); } }, { rootMargin: '700px 0px' });
        fio.observe(fonts);
      } else {
        applyFonts();
      }
      this.applyFontSamples = applyFonts;
    }

    var pats = $('[data-patterns]', root);
    if (pats) {
      pats.innerHTML = '';
      S.data.PATTERNS.forEach(function (p) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'chip chip--sm';
        b.setAttribute('role', 'radio');
        b.dataset.key = 'pattern';
        b.dataset.value = p.id;
        b.textContent = p.name;
        pats.appendChild(b);
      });
    }

    // Design tabs
    var tabs = $('[data-design-tabs]', root);
    if (tabs) {
      var defs = [['all', NL.strings.tabAll || 'Kaikki']].concat((S.designGroups || []).map(function (g) { return [g[0], g[1]]; }));
      tabs.innerHTML = defs.map(function (t) {
        return '<button type="button" role="tab" class="dtab" data-design-tab="' + t[0] + '" aria-selected="false">' + esc(t[1]) + '</button>';
      }).join('');
    }
    this.renderGrid();

    // Art groups + icons
    var groups = $('[data-art-groups]', root);
    if (groups) {
      groups.innerHTML = (S.artGroups || []).map(function (g) {
        return '<button type="button" class="chip" data-art-group="' + g[0] + '">' + esc(g[1]) + '</button>';
      }).join('');
    }
    this.buildArt();
  };

  // The grid of designs: buttons only, the stickers are drawn by paintTiles once the fonts are ready.
  Designer.prototype.renderGrid = function () {
    var grid = $('[data-design-grid]', this.root);
    if (!grid) return;
    var st = this.state;
    var list = this.designsOf(st.tab), size = this.pageSize();
    var pages = Math.max(1, Math.ceil(list.length / size));
    st.page = Math.max(0, Math.min(st.page, pages - 1));
    var items = list.slice(st.page * size, st.page * size + size);
    if (!items.length) {
      grid.innerHTML = '<p class="dgrid__empty">' + esc(tpl(NL.strings.searchEmpty || '', { q: st.q })) + '</p>';
      this.renderPager(1);
      return;
    }
    grid.innerHTML = items.map(function (d) {
      var dots = '';
      if (d.c) dots = '<span class="dtile__dots" aria-hidden="true">' + d.c.slice(0, 6).map(function (c) { return '<i style="background:' + c[0] + '"></i>'; }).join('') + '</span>';
      return '<button type="button" class="dtile" role="radio" aria-checked="false" data-key="design" data-value="' + d.id + '" title="' + esc(d.n) + '" aria-label="' +
        esc(tpl(NL.strings.designLabel || '{{ name }}', { name: d.n })) + '"><span class="dtile__art"></span>' + dots + '</button>';
    }).join('');
    $$('[data-design-tab]', this.root).forEach(function (t) {
      var on = t.dataset.designTab === st.tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    this.renderPager(pages);
    this.syncPickers();
    this.paintTiles();
  };

  Designer.prototype.renderPager = function (pages) {
    var box = $('[data-design-pager]', this.root);
    if (!box) return;
    var st = this.state;
    if (pages < 2) { box.innerHTML = ''; return; }
    var mid;
    if (pages <= 12) {
      mid = '<span class="dpager__dots" role="group">';
      for (var i = 0; i < pages; i++) {
        mid += '<button type="button" class="dpager__dot' + (i === st.page ? ' is-active' : '') + '" data-page="' + i + '" aria-label="' + esc(tpl(NL.strings.pagerStatus, { page: i + 1, pages: pages })) + '"' + (i === st.page ? ' aria-current="true"' : '') + '></button>';
      }
      mid += '</span>';
    } else {
      mid = '<span class="visually-hidden" aria-live="polite">' + esc(tpl(NL.strings.pagerStatus || '{{ page }} / {{ pages }}', { page: st.page + 1, pages: pages })) + '</span>';
    }
    var arrow = function (dir, label, icon, off) {
      return '<button type="button" class="dpager__btn" data-page="' + dir + '" aria-label="' + esc(label) + '"' + (off ? ' disabled' : '') + '>' +
        '<svg viewBox="0 0 256 256" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="' + icon + '"/></svg></button>';
    };
    var left = 'M165.66 202.34a8 8 0 0 1-11.32 11.32l-80-80a8 8 0 0 1 0-11.32l80-80a8 8 0 0 1 11.32 11.32L91.31 128Z';
    var right = 'M181.66 133.66l-80 80a8 8 0 0 1-11.32-11.32L164.69 128 90.34 53.66a8 8 0 0 1 11.32-11.32l80 80a8 8 0 0 1 0 11.32Z';
    box.innerHTML = arrow('prev', NL.strings.pagerPrev || '', left, st.page === 0) + mid + arrow('next', NL.strings.pagerNext || '', right, st.page === pages - 1);
  };

  /* ---------- Design library: every design in one dialog ------------------------------------------------ */

  Designer.prototype.initLibrary = function () {
    var self = this, root = this.root;
    var dlg = $('[data-library]', root), openBtn = $('[data-library-open]', root);
    if (!openBtn || !dlg || typeof dlg.showModal !== 'function' || !(S.designs && S.designs.length)) return;
    openBtn.hidden = false;
    var total = $('[data-library-total]', root);
    if (total) total.textContent = '(' + S.designs.length + ')';

    var grid = $('[data-library-grid]', dlg), tabsBox = $('[data-library-tabs]', dlg), search = $('[data-library-search]', dlg);
    var count = $('[data-library-count]', dlg), scroller = $('[data-library-scroll]', dlg), sentinel = $('[data-library-sentinel]', dlg);
    var lib = { tab: 'all', q: '', list: [], shown: 0, gen: 0, busy: false, painted: Promise.resolve(), arrowing: false };
    var CHUNK = 24, AHEAD = 400, typing;

    var defs = [['all', NL.strings.tabAll || 'Kaikki']].concat((S.designGroups || []).map(function (g) { return [g[0], g[1]]; }));
    tabsBox.innerHTML = defs.map(function (t) {
      return '<button type="button" role="tab" class="dtab" data-lib-tab="' + t[0] + '" aria-selected="false">' + esc(t[1]) + '</button>';
    }).join('');

    function filtered() {
      return (S.designs || []).filter(function (d) {
        if (lib.q) return d.n.toLowerCase().indexOf(lib.q) > -1 || d.id.indexOf(lib.q) > -1;
        return lib.tab === 'all' || d.g === lib.tab;
      });
    }
    function status() {
      if (!count) return;
      var n = lib.list.length;
      count.textContent = tpl(n === 1 && NL.strings.libraryCountOne ? NL.strings.libraryCountOne : (NL.strings.libraryCount || '{{ total }}'), { total: n });
    }
    // Adds the next chunk of tiles and draws them with the visitor's own name
    function more() {
      var next = lib.list.slice(lib.shown, lib.shown + CHUNK);
      if (!next.length) return;
      lib.shown += next.length;
      var before = grid.children.length;
      grid.insertAdjacentHTML('beforeend', next.map(function (d) {
        var dots = d.c ? '<span class="dtile__dots" aria-hidden="true">' + d.c.slice(0, 6).map(function (c) { return '<i style="background:' + c[0] + '"></i>'; }).join('') + '</span>' : '';
        return '<button type="button" class="dtile dtile--lib" role="radio" aria-checked="false" data-key="design" data-value="' + d.id + '" title="' + esc(d.n) + '" aria-label="' +
          esc(tpl(NL.strings.designLabel || '{{ name }}', { name: d.n })) + '"><span class="dtile__art"></span>' + dots + '<span class="dtile__name">' + esc(d.n) + '</span></button>';
      }).join(''));
      self.syncPickers();
      // Tiles only get their height once drawn, so the next chunk waits for that (otherwise far too many are added at once)
      var gen = lib.gen;
      lib.busy = true;
      lib.painted = self.paintTiles(grid, Array.prototype.slice.call(grid.children, before)).then(function () {
        if (gen !== lib.gen) return;
        lib.busy = false;
        if (dlg.open) fill();
      });
    }
    // The grid is endless: a chunk is added whenever the end of the list is within reach of the scrolled area
    function fill() {
      if (lib.busy || lib.shown >= lib.list.length) return;
      if (sentinel.getBoundingClientRect().top <= scroller.getBoundingClientRect().bottom + AHEAD) more();
    }
    function render() {
      lib.list = filtered();
      Array.prototype.forEach.call(tabsBox.children, function (b) {
        var on = b.getAttribute('data-lib-tab') === lib.tab && !lib.q;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      lib.shown = 0; lib.gen++; lib.busy = false;
      grid.innerHTML = '';
      scroller.scrollTop = 0;
      status();
      if (!lib.list.length) {
        grid.innerHTML = '<p class="dgrid__empty">' + esc(tpl(NL.strings.searchEmpty || '', { q: lib.q })) + '</p>';
        return;
      }
      more();
    }
    function open() {
      var st = self.state;
      lib.tab = 'all'; lib.q = ''; search.value = '';
      dlg.showModal();
      document.body.classList.add('is-locked');
      render();
      // Opens at the design that is chosen now (the tiles keep their size only once drawn, so the scroll waits for that)
      var cur = st.custom ? null : st.design, idx = -1;
      for (var i = 0; i < lib.list.length; i++) if (lib.list[i].id === cur) { idx = i; break; }
      if (idx < 0) return;
      while (idx >= lib.shown) more();
      lib.painted.then(function () {
        var tile = $('.dtile[data-value="' + cur + '"]', grid);
        if (tile && dlg.open) tile.scrollIntoView({ block: 'center' });
      });
    }

    openBtn.addEventListener('click', open);
    $('[data-library-close]', dlg).addEventListener('click', function () { dlg.close(); });
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
    dlg.addEventListener('close', function () { document.body.classList.remove('is-locked'); openBtn.focus({ preventScroll: true }); });
    tabsBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-lib-tab]');
      if (!b) return;
      lib.tab = b.getAttribute('data-lib-tab'); lib.q = ''; search.value = '';
      render();
    });
    search.addEventListener('input', function () {
      clearTimeout(typing);
      typing = setTimeout(function () { lib.q = search.value.trim().toLowerCase(); render(); }, 160);
    });
    scroller.addEventListener('scroll', fill, { passive: true });
    // The click handler of the form does the choosing. Picking with the pointer closes the library, arrow keys only move the choice.
    grid.addEventListener('keydown', function (e) {
      if (/^Arrow/.test(e.key)) { lib.arrowing = true; setTimeout(function () { lib.arrowing = false; }, 0); }
    }, true);
    grid.addEventListener('click', function (e) {
      if (lib.arrowing || !e.target.closest('.dtile')) return;
      setTimeout(function () { self.gotoDesign(self.state.design); dlg.close(); }, 180);
    });
  };

  Designer.prototype.buildArt = function () {
    var box = $('[data-icons]', this.root);
    if (!box) return;
    var st = this.state, group = st.artGroup, idx = S.artIndex || {};
    var html = '<button type="button" class="icon-pick icon-pick--none" role="radio" data-key="art" data-value="" aria-label="' + esc(NL.strings.artNone || 'Ei koristetta') + '" title="' + esc(NL.strings.artNone || 'Ei koristetta') + '"><svg viewBox="0 0 256 256" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M205.66 194.34a8 8 0 0 1-11.32 11.32L128 139.31l-66.34 66.35a8 8 0 0 1-11.32-11.32L116.69 128 50.34 61.66a8 8 0 0 1 11.32-11.32L128 116.69l66.34-66.35a8 8 0 0 1 11.32 11.32L139.31 128Z"/></svg></button>';
    Object.keys(idx).forEach(function (id) {
      if (idx[id][0] !== group) return;
      var a = S.art[id];
      html += '<button type="button" class="icon-pick" role="radio" data-key="art" data-value="' + id + '" aria-label="' + esc(idx[id][1]) + '" title="' + esc(idx[id][1]) + '">' +
        (a ? '<svg viewBox="' + a[0] + '" width="34" height="34" aria-hidden="true">' + a[1] + '</svg>' : '<span class="icon-pick__ph" aria-hidden="true"></span>') + '</button>';
    });
    box.innerHTML = html;
    $$('[data-art-group]', this.root).forEach(function (b) { b.classList.toggle('is-active', b.dataset.artGroup === group); });
    this.syncPickers();
  };

  /* ---------- Events --------------------------------------------------------------------------------- */

  Designer.prototype.bind = function () {
    var self = this;
    var st = this.state;
    var root = this.root;

    // Prefill text from state
    if (this.el.text) this.el.text.value = st.text;
    if (this.el.text2) this.el.text2.value = st.text2;
    if (this.el.text3) this.el.text3.value = st.text3;
    if (this.el.upper) this.el.upper.checked = !!st.upper;

    var onText = function () {
      st.text = self.el.text.value.slice(0, self.maxChars);
      if (self.el.text2) st.text2 = self.el.text2.value.slice(0, self.maxChars);
      if (self.el.text3) st.text3 = self.el.text3.value.slice(0, self.maxChars);
      if (self.el.upper) st.upper = self.el.upper.checked;
      if (st.text.trim()) self.showNameError(false);
      self.syncLines();
      self.schedulePaint();
    };
    if (this.el.text) this.el.text.addEventListener('input', onText);
    if (this.el.text2) this.el.text2.addEventListener('input', onText);
    if (this.el.text3) this.el.text3.addEventListener('input', onText);
    if (this.el.upper) this.el.upper.addEventListener('change', onText);
    if (this.el.plain) this.el.plain.addEventListener('change', function () { self.setPlain(self.el.plain.checked); });

    root.addEventListener('click', function (e) {
      var pick = e.target.closest('[data-key][data-value]');
      if (pick && root.contains(pick)) { self.choose(pick.dataset.key, pick.dataset.value); return; }
      var dtab = e.target.closest('[data-design-tab]');
      if (dtab) {
        st.tab = dtab.dataset.designTab; st.page = 0;
        if (st.q) { st.q = ''; var sb = $('[data-design-search]', root); if (sb) sb.value = ''; }
        var list = self.designsOf(st.tab), size = self.pageSize();
        for (var i = 0; i < list.length; i++) if (list[i].id === st.design && !st.custom) { st.page = Math.floor(i / size); break; }
        self.renderGrid();
        return;
      }
      var pg = e.target.closest('[data-page]');
      if (pg && !pg.disabled) {
        var v = pg.dataset.page;
        st.page = v === 'prev' ? st.page - 1 : (v === 'next' ? st.page + 1 : parseInt(v, 10));
        self.renderGrid();
        return;
      }
      if (e.target.closest('[data-surprise]')) { self.surprise(); return; }
      if (e.target.closest('[data-reset-design]')) {
        self.applyDesign(st.design);
        self.syncPickers(); self.schedulePaint();
        return;
      }
      var grp = e.target.closest('[data-art-group]');
      if (grp) { st.artGroup = grp.dataset.artGroup; self.buildArt(); return; }
      var tab = e.target.closest('[data-tab]');
      if (tab) { self.setView(tab.dataset.tab); return; }
      var variant = e.target.closest('[data-variant]');
      if (variant) { self.setVariant(variant.dataset.variant); return; }
      var thumb = e.target.closest('[data-thumb]');
      if (thumb) { self.setSlide(parseInt(thumb.dataset.thumb, 10)); }
    });

    // Arrow-key navigation inside radio groups
    root.addEventListener('keydown', function (e) {
      var radio = e.target.closest('[role="radio"]');
      if (!radio || ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].indexOf(e.key) < 0) return;
      var group = radio.closest('[role="radiogroup"]');
      if (!group) return;
      var items = $$('[role="radio"]:not([disabled])', group);
      var i = items.indexOf(radio);
      i = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
      e.preventDefault();
      items[i].focus();
      items[i].click();
    });

    // The art panel loads the rest of the icons when it opens
    var artPanel = $('[data-step="art"]', root);
    if (artPanel) {
      artPanel.addEventListener('toggle', function () {
        if (artPanel.open) S.loadArt().then(function () { self.buildArt(); });
      });
    }

    // Grid page size follows the viewport
    if (window.matchMedia) {
      var mq = window.matchMedia('(max-width: 519px)');
      var onMq = function () { self.gotoDesign(st.design); };
      if (mq.addEventListener) mq.addEventListener('change', onMq); else if (mq.addListener) mq.addListener(onMq);
    }

    // Design search
    var search = $('[data-design-search]', root);
    if (search) {
      var typing;
      search.addEventListener('input', function () {
        clearTimeout(typing);
        typing = setTimeout(function () {
          st.q = search.value.trim().toLowerCase();
          st.tab = 'all'; st.page = 0;
          self.renderGrid();
        }, 160);
      });
    }

    // Price ladder: the button shows what these sheets add to the order, counting the sheets already in the cart
    this.cartSheets = 0;
    if (this.isLadder()) {
      var setCart = function (cart) { self.cartSheets = ladderSheets(cart); self.updatePrice(); };
      document.addEventListener('cart:updated', function (e) { setCart(e.detail); });
      fetch(NL.routes.cartJson, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); }).then(setCart).catch(function () {});
    }

    // Quantity: the stepper and the tiers of the price ladder (1 to 5 sheets, "…" = another amount) set the same value
    if (this.el.qty) {
      this.el.qty.addEventListener('change', function () { self.setQty(self.el.qty.value); });
      var ladder = $('[data-ladder]', root);
      if (ladder) {
        ladder.addEventListener('click', function (e) {
          var tier = e.target.closest('[data-tier]');
          if (tier) { self.setQty(tier.dataset.tier); return; }
          if (e.target.closest('[data-tier-more]')) {
            // Any other amount is typed in the stepper, which is further down the page
            var q = self.el.qty;
            q.scrollIntoView({ block: 'center', behavior: 'smooth' });
            q.focus({ preventScroll: true });
            q.select();
          }
        });
      }
    }

    // Photo controls
    this.bindPhoto();

    // Submit
    this.form.addEventListener('submit', function (e) { e.preventDefault(); self.submit(); });

    // "Add another name": keep the design, clear the name (siblings, classmates)
    var another = $('[data-add-another]', root);
    if (another) {
      another.addEventListener('click', function () {
        st.text = ''; st.text2 = ''; st.text3 = '';
        if (self.el.text) { self.el.text.value = ''; self.el.text.focus(); self.el.text.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        if (self.el.text2) self.el.text2.value = '';
        if (self.el.text3) self.el.text3.value = '';
        self.syncLines();
        self.showAdded(false);
        self.schedulePaint();
      });
    }
    if (this.el.text) this.el.text.addEventListener('input', function () { self.showAdded(false); });
  };

  Designer.prototype.choose = function (key, value) {
    var st = this.state;
    if (key === 'design') {
      this.applyDesign(value);
      if (!st.fontCustom) st.font = this.designFont(value);
      if (this.mode === 'photo') { st.fg = '#FFFFFF'; }
    } else if (key === 'font') {
      st.font = value; st.fontCustom = true;
    } else if (key === 'bg') {
      var c = S.byId(S.data.COLORS, value);
      if (!c) return;
      st.bg = c.hex; st.custom = true;
      if (st.fgCustom && S.contrast(st.bg, st.fg) < 1.8) st.fgCustom = false;
      if (!st.fgCustom) st.fg = S.bestFg(st.bg);
      if (st.pattern) st.pattern = S.resolveStyle({ bg: st.bg, pattern: st.pattern.id }).pattern;
    } else if (key === 'fg') {
      var t = S.byId(S.data.TEXT_COLORS, value);
      if (!t) return;
      st.fg = t.hex; st.fgCustom = true;
      if (this.mode !== 'photo') st.custom = true;
    } else if (key === 'pattern') {
      st.pattern = value === 'none' ? null : S.resolveStyle({ bg: st.bg, pattern: value }).pattern;
      st.custom = true;
    } else if (key === 'art') {
      st.art = value || null;
      if (st.art && !S.art[st.art]) this.needArt = true;
    }
    this.syncPickers();
    this.schedulePaint();
  };

  // Random design and font
  Designer.prototype.surprise = function () {
    var st = this.state, list = S.designs || [], pick;
    if (!list.length) return;
    do { pick = list[Math.floor(Math.random() * list.length)].id; } while (pick === st.design && list.length > 1);
    this.applyDesign(pick);
    var fonts = S.data.FONTS;
    st.font = fonts[Math.floor(Math.random() * fonts.length)].id;
    st.tab = 'all'; st.q = '';
    var sb = $('[data-design-search]', this.root); if (sb) sb.value = '';
    this.gotoDesign(pick, true);
    this.renderGrid();
    this.schedulePaint();
  };

  // Reflect state in the pickers (aria-checked + roving tabindex + value labels).
  Designer.prototype.syncPickers = function () {
    var st = this.state;
    var root = this.root;
    var same = function (a, b) { return String(a || '').toLowerCase() === String(b || '').toLowerCase(); };
    $$('[role="radio"][data-key]', root).forEach(function (b) {
      var key = b.dataset.key, val = b.dataset.value, on = false;
      if (key === 'design') on = !st.custom && st.design === val;
      else if (key === 'font') on = st.font === val;
      else if (key === 'bg') { var c = S.byId(S.data.COLORS, val); on = !!c && same(c.hex, st.bg); }
      else if (key === 'fg') { var t = S.byId(S.data.TEXT_COLORS, val); on = !!t && same(t.hex, st.fg); }
      else if (key === 'pattern') on = (st.pattern ? st.pattern.id : 'none') === val;
      else if (key === 'art') on = (st.art || '') === val;
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.classList.toggle('is-active', on);
      b.tabIndex = on ? 0 : -1;
    });
    // If nothing in a group is checked, keep the first one focusable.
    $$('[role="radiogroup"]', root).forEach(function (g) {
      if (!$('[role="radio"][aria-checked="true"]', g)) {
        var first = $('[role="radio"]', g);
        if (first) first.tabIndex = 0;
      }
    });

    var d = st.design && S.designById(st.design);
    var f = S.fontOf(st.font);
    var cname = S.data.COLORS.filter(function (c) { return same(c.hex, st.bg); })[0];
    var tname = S.data.TEXT_COLORS.filter(function (c) { return same(c.hex, st.fg); })[0];
    if (this.el.designName) {
      this.el.designName.textContent = d ? (st.custom ? tpl(NL.strings.designEdited || '{{ name }}', { name: d.n }) : d.n) : '';
    }
    if (this.el.fontName) this.el.fontName.textContent = f ? f.name : '';
    if (this.el.artName) {
      var a = st.art && S.artIndex[st.art];
      this.el.artName.textContent = a ? a[1] : (NL.strings.artNone || 'Ei koristetta');
    }
    if (this.el.colorName) this.el.colorName.textContent = cname ? cname.name : '';
    this.el.textColorNames.forEach(function (el) { el.textContent = tname ? tname.name : ''; });
    if (this.el.customName) {
      var bits = [];
      if (st.custom) bits.push(cname ? cname.name : st.bg);
      if (st.custom && st.pattern) bits.push((S.byId(S.data.PATTERNS, st.pattern.id) || {}).name);
      this.el.customName.textContent = bits.length ? bits.filter(Boolean).join(', ') : (NL.strings.customSummary || '');
    }
    if (this.el.resetDesign) this.el.resetDesign.hidden = !(st.custom || st.fgCustom);
    if (this.el.plain) this.el.plain.checked = this.isPlain();
  };

  Designer.prototype.setView = function (view) {
    this.state.view = view;
    var root = this.root;
    $$('[data-tab]', root).forEach(function (t) {
      var on = t.dataset.tab === view;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    $$('[data-view]', root).forEach(function (v) {
      var on = v.dataset.view === view;
      v.hidden = !on;
      v.classList.toggle('is-active', on);
    });
  };

  Designer.prototype.setSlide = function (i) {
    var root = this.root;
    $$('[data-slide]', root).forEach(function (s, idx) { s.classList.toggle('is-active', idx === i); });
    $$('[data-thumb]', root).forEach(function (t) { t.classList.toggle('is-active', parseInt(t.dataset.thumb, 10) === i); });
  };

  Designer.prototype.setVariant = function (id) {
    this.variantId = parseInt(id, 10);
    this.variant = this.currentVariant();
    this.spec = resolveSpec(this.config, this.variant && this.variant.title);
    if (this.el.variantId) this.el.variantId.value = this.variantId;
    $$('[data-variant]', this.root).forEach(function (b) {
      var on = String(b.dataset.variant) === String(id);
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    if (this.el.variantName && this.variant) this.el.variantName.textContent = this.variant.title;
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('variant', id);
      window.history.replaceState({}, '', u.toString());
    } catch (e) {}
    this.applySpec();
    this.gotoDesign(this.state.design);
    this.schedulePaint();
  };

  // The second and third line: mini labels have two lines at most (name and e.g. a phone number, 20.10.2026). The third line
  // (care homes: name, home, room) shows once the second one is in use, so the form stays short for the many who need one or two lines.
  Designer.prototype.syncLines = function () {
    var st = this.state;
    var isMini = this.spec.shape === 'mini' || (this.spec.h < 8);
    if (isMini) {
      st.text3 = '';
      if (this.el.text3) this.el.text3.value = '';
    }
    if (this.el.text2Wrap) this.el.text2Wrap.hidden = false;
    if (this.el.text3Wrap) this.el.text3Wrap.hidden = isMini || !(st.text2.trim() || st.text3.trim());
    // the second line of a mini label is mostly a phone number
    if (this.el.text2) {
      var ph = this.el.text2.getAttribute(isMini ? 'data-placeholder-mini' : 'data-placeholder');
      if (ph) this.el.text2.placeholder = ph;
    }
  };

  // Things that depend on the shape: second and third line, upload label, price, availability.
  Designer.prototype.applySpec = function () {
    this.syncLines();
    this.updatePrice();
    var v = this.variant;
    if (v && this.el.submit) {
      this.el.submit.disabled = !v.available;
      if (this.el.submitLabel) this.el.submitLabel.textContent = v.available ? (NL.strings.addToCart || 'Lisää ostoskoriin') : (NL.strings.soldOut || 'Loppu');
    }
    this.syncPickers();
  };

  // Sheets of the collection `arkit` follow the price ladder (kits do not)
  Designer.prototype.isLadder = function () {
    var L = NL.ladder;
    return !!(L && L.handles && this.config.handle && L.handles.indexOf(this.config.handle) > -1 && !(this.config.cfg && this.config.cfg.items));
  };

  function ladderSheets(cart) {
    var L = NL.ladder, n = 0;
    ((cart && cart.items) || []).forEach(function (it) { if (L.handles.indexOf(it.handle) > -1) n += it.quantity; });
    return n;
  }

  // Discount (cents) for m sheets in the order: the first sheet at full price, every further one `off` cents cheaper, up to `max` sheets
  function ladderOff(m) {
    var L = NL.ladder;
    return m < 1 ? 0 : L.off * (Math.min(m, L.max) - 1);
  }

  // The one place the quantity changes: the input, the state, the button price and the lit tier follow
  Designer.prototype.setQty = function (n) {
    var v = Math.max(1, Math.min(99, parseInt(n, 10) || 1));
    if (this.el.qty) this.el.qty.value = v;
    this.state.qty = v;
    this.updatePrice();
  };

  Designer.prototype.updatePrice = function () {
    var v = this.variant;
    if (!v) return;
    var qty = this.state.qty;
    // The price of these sheets on the price ladder, the same number as on the tier of that quantity
    var total = v.price * qty;
    if (this.isLadder()) total -= ladderOff(qty);
    if (this.el.unit) this.el.unit.textContent = U.money(v.price);
    if (this.el.total) this.el.total.textContent = U.money(total);
    // The tiers of the price ladder follow the quantity of the stepper; "…" lights up when it is more than the tiers show
    var tiers = $$('[data-tier]', this.root);
    if (tiers.length) {
      tiers.forEach(function (t) {
        var on = parseInt(t.dataset.tier, 10) === qty;
        t.classList.toggle('is-current', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var more = $('[data-tier-more]', this.root);
      if (more) more.classList.toggle('is-current', qty > tiers.length);
    }
    // Sheets already in the cart count towards the ladder (the cart applies the discount), so the cart grows by less than the price above
    var inCart = $('[data-ladder-cart]', this.root);
    if (inCart) {
      var m = this.isLadder() ? this.cartSheets : 0;
      var grow = v.price * qty - (ladderOff(m + qty) - ladderOff(m));
      var line = m > 0 && grow < total ? tpl((m === 1 ? NL.strings.ladderInCartOne : NL.strings.ladderInCartOther) || '', { count: m, amount: U.money(grow) }) : '';
      inCart.textContent = line;
      inCart.hidden = !line;
    }
    var per = $('[data-per-sticker]', this.root);
    if (per) {
      if (this.spec.perSheet && !this.spec.items) {
        per.hidden = false;
        per.textContent = '≈ ' + (v.price / this.spec.perSheet).toFixed(1).replace('.', ',') + ' snt / tarra';
      } else {
        per.hidden = true;
      }
    }
  };

  /* ---------- Rendering ------------------------------------------------------------------------------- */

  Designer.prototype.schedulePaint = function () {
    var self = this;
    if (this._raf) return;
    this._raf = requestAnimationFrame(function () { self._raf = null; self.paint(); });
  };

  // One "group" per sheet: a normal product has one, a kit has one per included sheet.
  Designer.prototype.groups = function () {
    var spec = this.spec;
    if (spec.items) return spec.items;
    return [{ shape: spec.shape, w: spec.w, h: spec.h, perSheet: spec.perSheet, format: spec.format, label: '' }];
  };

  Designer.prototype.optsFor = function (shapeKey, group, tone) {
    var st = this.state;
    var g = group || this.spec;
    var isThis = g.shape === shapeKey;
    var ts = this.toneStyle(tone || 0);
    return {
      shape: shapeKey,
      w: isThis ? g.w : null, h: isThis ? g.h : null,
      text: st.text || this.sample,
      text2: st.text2, text3: st.text3,
      font: st.font, upper: st.upper,
      bg: ts.bg, fg: ts.fg, pattern: ts.pattern || 'none',
      art: this.mode === 'photo' ? (st.image ? '' : 'camera') : (ts.art || ''),
      image: st.image,
      preview: true
    };
  };

  // Which stickers the stage shows for one sheet: [{ shape, tone }]
  Designer.prototype.stageItems = function (g) {
    var pal = this.palette();
    var M = S.data.MIXED_TONES;
    if (g.shape === 'mixed') {
      return ['round', 'square', 'oblong'].map(function (sh, i) { return { shape: sh, tone: pal ? i : (M[sh] || 0) }; });
    }
    if (pal && !this.state.image) {
      var n = Math.min(3, pal.length), out = [];
      for (var i = 0; i < n; i++) out.push({ shape: g.shape, tone: i, fan: true });
      return out;
    }
    return [{ shape: g.shape, tone: 0 }];
  };

  Designer.prototype.paint = function () {
    var self = this, st = this.state, spec = this.spec;
    var groups = this.groups();
    var isKit = !!spec.items;
    var sampleText = st.text || this.sample;
    // a package item with more than one sheet says so (Koulupaketti: 2 × Sekoitusarkki)
    var itemLabel = function (g) { return (g.qty > 1 ? g.qty + ' × ' : '') + g.label; };

    if (this.el.counter) this.el.counter.textContent = st.text.length + '/' + this.maxChars;
    if (st.art && !S.art[st.art] && this.mode !== 'photo') S.loadArt().then(function () { self.needArt = false; self.schedulePaint(); });

    S.ready(sampleText + st.text2 + st.text3, [st.font]).then(function () {
      var infos = [];
      var single = '', actual = '', sheets = '', mini = '';
      var total = groups.length;
      var fanned = false, mixedSet = false;
      var sceneStickers = [];

      groups.forEach(function (g) {
        var items = self.stageItems(g);
        var widths = items.map(function (it) { return it.shape === g.shape ? g.w : S.data.SHAPES[it.shape].w; });
        var mmSum = widths.reduce(function (a, b) { return a + b; }, 0);
        var fan = items.length > 1 && items[0].fan;
        if (fan) fanned = true;
        if (g.shape === 'mixed') mixedSet = true;
        var stickers = '';
        items.forEach(function (it, i) {
          var r = S.render(self.optsFor(it.shape, g, it.tone));
          if (g === groups[0]) sceneStickers.push({ svg: r.svg, w: it.shape === g.shape ? g.w : S.data.SHAPES[it.shape].w, h: it.shape === g.shape ? g.h : S.data.SHAPES[it.shape].h, shape: it.shape });
          if (it.tone === 0 || i === 0) infos.push(r.info);
          stickers += '<div class="stage__sticker stage__sticker--' + it.shape + (fan ? ' stage__sticker--fan' : '') + '" style="--mm:' + widths[i] + '">' + r.svg + '</div>';
          // the slim bar on phones: every label of a plain product, the first label of each sheet of a kit
          if ((g === groups[0] && !isKit) || (isKit && i === 0)) mini += '<div class="stage__sticker stage__sticker--' + it.shape + '">' + S.fresh(r.svg) + '</div>';
          if (!fan || i === 0) actual += '<div class="stage__actual-sticker" style="width:' + (widths[i] + 1) + 'mm">' + S.fresh(r.svg) + '</div>';
        });

        var caption = '';
        if (isKit) {
          caption = '<p class="stage__caption"><strong>' + esc(itemLabel(g)) + '</strong>' +
            (g.perSheet ? '<span>' + esc(tpl(NL.strings.perSheet, { count: g.perSheet })) + '</span>' : '') + '</p>';
          var k = Math.min(3.2, 86 / mmSum);
          single += '<div class="stage__group"><div class="stage__stickers stage__stickers--kit" style="--mmk:' + k.toFixed(2) + 'cqw">' + stickers + '</div>' + caption + '</div>';
        } else {
          single = '<div class="stage__stickers stage__stickers--' + (g.shape === 'mixed' ? 'mixed' : (fan ? 'fan' : g.shape)) + '">' + stickers + '</div>';
        }

        sheets += '<div class="stage__sheet-item">' + S.sheet({
          shape: g.shape === 'mixed' ? 'mixed' : g.shape, format: g.format,
          text: st.text || self.sample, text2: st.text2, text3: st.text3, font: st.font, upper: st.upper,
          styles: self.styles(), mix: self.mixFor(g), image: self.sheetImage()
        }) + (isKit ? '<p class="stage__caption"><strong>' + esc(itemLabel(g)) + '</strong></p>' : '') + '</div>';
      });

      if (self.el.single) self.el.single.innerHTML = isKit ? '<div class="stage__kit" style="--n:' + total + '">' + single + '</div>' : single;
      if (self.el.mini) self.el.mini.innerHTML = mini;
      if (self.el.actual) self.el.actual.innerHTML = actual;
      if (self.el.use && S.mockup) {
        // The squirrels carry the visitor's stickers: the family for plain stickers, the one with the sweater for iron-on labels
        var scene = spec.scene || 'school';
        if (scene === 'school') scene = 'perhe';
        else if (scene === 'clothes') scene = 'pulla';
        if (S.mockupInto) S.mockupInto(self.el.use, scene, sceneStickers, { label: NL.strings.useLabel });
        else self.el.use.innerHTML = S.mockup(scene, sceneStickers, { label: NL.strings.useLabel });
        var useNote = $('[data-use-note]', self.root);
        if (useNote) useNote.textContent = (scene === 'perhe' ? NL.strings.useNoteOrava : scene === 'pulla' ? NL.strings.useNotePulla : NL.strings.useNote) || '';
      }
      if (self.el.sheet) self.el.sheet.innerHTML = '<div class="stage__sheets stage__sheets--' + total + '">' + sheets + '</div>';
      if (self.el.count) {
        if (isKit) {
          self.el.count.textContent = groups.map(itemLabel).join(' + ');
        } else {
          var fmtInfo = S.data.FORMATS[spec.format];
          var fmtLabel = (fmtInfo && fmtInfo.label) || spec.format;
          self.el.count.textContent = spec.perSheet
            ? tpl(NL.strings.perSheet, { count: spec.perSheet }) + ' · ' + fmtLabel
            : fmtLabel;
        }
      }
      if (self.el.size) self.el.size.textContent = self.sizeLabel();

      self.updateWarnings(infos, fanned ? NL.strings.fanNote : (mixedSet && self.state.design ? NL.strings.mixedNote : ''));
      self.updateFit();
      self.updateFontSamples();
      self.paintTiles();
      self.syncProps();
      self.save();
      if (st.image) self.bindStageDrag();
    });
  };

  // Draws the stickers of the design grid: the visitor's own name and font on every design.
  Designer.prototype.paintTiles = function (gridEl, only) {
    var grid = gridEl || $('[data-design-grid]', this.root);
    if (!grid) return;
    var self = this, st = this.state;
    var tiles = function () { return only || $$('.dtile', grid); };
    var g0 = this.groups()[0];
    var shape = g0.shape === 'mixed' ? 'round' : g0.shape;
    var text = st.text || this.sample;
    var tileFont = function (d) { return st.fontCustom ? st.font : (d.f && S.byId(S.data.FONTS, d.f) ? d.f : st.font); };
    var fonts = [st.font];
    tiles().forEach(function (btn) { var d = S.designById(btn.dataset.value); if (d && fonts.indexOf(tileFont(d)) < 0) fonts.push(tileFont(d)); });
    return S.ready(text, fonts).then(function () {
      tiles().forEach(function (btn) {
        var d = S.designById(btn.dataset.value);
        if (!d) return;
        var t = S.toneOf(d, 0);
        var r = S.render({
          shape: shape, w: g0.shape === shape ? g0.w : null, h: g0.shape === shape ? g0.h : null,
          text: text, text2: '', font: tileFont(d), upper: st.upper,
          bg: t.bg, fg: t.fg, pattern: S.patternOf(d) || 'none', art: d.c ? '' : (d.a || ''),
          preview: true
        });
        var art = $('.dtile__art', btn);
        if (art) art.innerHTML = r.svg;
      });
    });
  };

  Designer.prototype.sizeLabel = function () {
    var spec = this.spec;
    // a package counts its sheets, not its items (Koulupaketti = 2 sekoitusarkkia + minitarrat = 3 arkkia)
    if (spec.items) return spec.items.reduce(function (n, i) { return n + (i.qty || 1); }, 0) + ' ' + (NL.strings.sheetsWord || 'arkkia');
    if (spec.shape === 'mixed') return '⌀ 18 · 18 × 18 · 22 × 12 mm';
    var f = function (n) { return String(n).replace('.', ','); };
    if (spec.shape === 'round') return '⌀ ' + f(spec.w) + ' mm';
    return f(spec.w) + ' × ' + f(spec.h) + ' mm';
  };

  Designer.prototype.updateFontSamples = function () {
    var text = this.state.upper ? (this.state.text || this.sample).toLocaleUpperCase('fi') : (this.state.text || this.sample);
    $$('.font-card__sample', this.root).forEach(function (el) { el.textContent = text; });
  };

  // Resolution (ppi) of the visible part of the picture on the printed sticker, null without a picture.
  // Same cover crop as sticker.js imageMarkup: the picture fills the sticker shape, zoom shows 1/zoom of it.
  Designer.prototype.photoPpi = function () {
    var im = this.state.image, g = this.groups()[0];
    if (!im || !im.nw || !im.nh || !g || !g.w || !g.h) return null;
    var cw = im.nw / im.nh > g.w / g.h ? im.nh * g.w / g.h : im.nw;
    return cw / Math.max(1, im.zoom || 1) / (g.w / 25.4);
  };

  Designer.prototype.updateWarnings = function (infos, note) {
    var list = this.el.warnings;
    if (!list) return;
    var msgs = [];
    var small = infos.some(function (i) { return i.tooSmall; });
    var shrunk = infos.some(function (i) { return i.shrunk; });
    var lowc = !this.state.image && infos.some(function (i) { return i.contrast < 3 && !i.patterned; });
    var ppi = this.photoPpi();
    if (small) msgs.push({ type: 'warn', text: NL.strings.textTooSmall });
    else if (shrunk) msgs.push({ type: 'info', text: NL.strings.textShrinks });
    // Several lines on a decorated label get small (the art takes room): point at the tick box that gives the room back
    var st = this.state;
    if ((small || shrunk) && (st.text2.trim() || st.text3.trim()) && this.el.plain && !this.isPlain() && NL.strings.plainHint) msgs.push({ type: 'info', text: NL.strings.plainHint });
    if (lowc) msgs.push({ type: 'warn', text: NL.strings.lowContrast });
    if (ppi !== null && ppi < PHOTO_MIN_PPI) msgs.push({ type: 'warn', text: NL.strings.imageLowRes });
    if (note) msgs.push({ type: 'info', text: note });
    list.innerHTML = msgs.map(function (m) {
      return '<li class="stage__warning stage__warning--' + m.type + '">' + esc(m.text || '') + '</li>';
    }).join('');
  };

  /* ---------- Fit guard (assets/fit-guard.js) ----------------------------------------------------------------- */

  // Can the production tool make this text? NL.fitGuard is the theme setting: 'off' (the scripts are not even loaded), 'warn' (a note,
  // the order goes through) or 'block' (a note, and the add to cart button refuses). Whatever goes wrong inside the guard, the order
  // is not stopped: no metrics, an unknown product or an exception all mean "say nothing".
  Designer.prototype.fitMode = function () {
    var m = NL.fitGuard;
    return window.NLFit && (m === 'warn' || m === 'block') ? m : 'off';
  };

  Designer.prototype.fitResult = function () {
    try {
      var v = this.variant || {};
      return window.NLFit.check(this.designJson(), {
        sku: v.sku, handle: this.config.handle, variant: v.title && v.title !== 'Default Title' ? v.title : '', S: S
      });
    } catch (e) {
      if (window.console && console.warn) console.warn('fit guard:', e);
      return null;
    }
  };

  // The notes for the visitor: { msgs: [text], first: the field to look at (0 name, 1 second line, 2 third line) }
  Designer.prototype.computeFit = function () {
    var out = { msgs: [], first: 0 };
    if (this.fitMode() === 'off') return out;
    var r = this.fitResult();
    if (!r) return out;
    var spec = this.spec, F = NL.strings;
    r.sheets.forEach(function (sh) {
      if (sh.status !== 'cut') return;
      var label = '';
      if (spec.items && sh.key) spec.items.forEach(function (it) { if (it.key === sh.key) label = it.label; });
      var where = label ? ' (' + label + ')' : '';
      if (!sh.cuts || !sh.cuts.length) { out.msgs.push(tpl(F.fitCutAny, { where: where })); return; }
      var fields = sh.cuts.map(function (c) { return tpl(F.fitFieldMax, { field: F.fitField[c.src] || F.fitField[0], max: c.max }); }).join(F.fitAnd);
      if (!out.msgs.length) out.first = sh.cuts[0].src;
      out.msgs.push(tpl(F.fitCut, { where: where, fields: fields }));
    });
    if (r.glyphs.length) out.msgs.push(tpl(F.fitGlyph, { chars: r.glyphs.join(' ') }));
    return out;
  };

  // Loads the metrics of the chosen font (once per font), then shows or clears the note and the state of the button
  Designer.prototype.updateFit = function () {
    var self = this, mode = this.fitMode();
    if (mode === 'off') return Promise.resolve();
    var F = window.NLFit;
    return F.loadFont(F.fontId(this.state.font)).then(function () {
      var fit = self.computeFit();
      var note = self.el.fitNote;
      if (note) {
        note.hidden = !fit.msgs.length;
        note.className = 'fit-note fit-note--' + (mode === 'block' ? 'error' : 'warn');
        note.textContent = fit.msgs.join(' ');
      }
      var btn = self.el.submit;
      if (btn) {
        var blocked = mode === 'block' && fit.msgs.length > 0;
        btn.classList.toggle('is-blocked', blocked);
        if (blocked) btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
      }
      // the refusal shown by the button follows the note (other form errors, e.g. a failed add to cart, are left alone)
      if (self.fitFormMsg && self.el.formError && self.el.formError.textContent === self.fitFormMsg) {
        self.fitFormMsg = fit.msgs[0] || '';
        self.showFormError(self.fitFormMsg);
      }
    });
  };

  /* ---------- Cart properties ------------------------------------------------------------------------------- */

  Designer.prototype.designJson = function () {
    var st = this.state, spec = this.spec;
    var shapeDef = S.data.SHAPES[spec.shape === 'mixed' ? 'round' : spec.shape] || {};
    var d = {
      v: 2,
      product: this.config.handle,
      shape: spec.shape,
      w: spec.w || shapeDef.w, h: spec.h || shapeDef.h,
      text: st.text.trim()
    };
    if (st.text2.trim()) d.text2 = st.text2.trim();
    if (st.text3.trim()) d.text3 = st.text3.trim();
    d.font = st.font;
    if (this.mode !== 'photo' && st.design) { d.design = st.design; if (st.custom) d.custom = 1; }
    d.bg = st.bg;
    d.fg = st.fg;
    if (st.pattern && !st.image) { d.pattern = { id: st.pattern.id, c: st.pattern.c, o: st.pattern.o }; if (st.pattern.c2) d.pattern.c2 = st.pattern.c2; }
    if (st.art && !st.image) d.art = st.art;
    if (st.upper) d.upper = 1;
    if (st.image) d.image = { zoom: round(st.image.zoom), dx: round(st.image.dx), dy: round(st.image.dy), nw: st.image.nw, nh: st.image.nh };

    // Tones: the other labels of a mixed sheet, or the colours of a palette design
    var groups = this.groups();
    var mixed = groups.some(function (g) { return g.shape === 'mixed'; });
    var pal = this.palette();
    if (!st.image && this.mode !== 'photo' && (mixed || pal)) {
      d.mix = pal ? (mixed ? 'cols' : 'rows') : 'shapes';
      if (pal) d.cols = this.styles().map(function (t) { return [t.bg, t.fg]; });
      else d.tones = [this.toneStyle(1), this.toneStyle(2)].map(function (t) { return [t.bg, t.fg]; });
    }

    if (spec.items) {
      d.shape = 'kit';
      delete d.w; delete d.h;
      d.kit = 1;
      d.items = spec.items.map(function (i) { return { key: i.key, shape: i.shape, w: i.w, h: i.h, perSheet: i.perSheet, format: i.format, qty: i.qty }; });
    } else {
      d.sheet = { format: spec.format, count: spec.perSheet || null };
    }
    if (this.variant && this.variant.title && this.variant.title !== 'Default Title') d.variant = this.variant.title;
    return d;
  };

  Designer.prototype.syncProps = function () {
    var st = this.state;
    var props = [];
    var d = st.design && S.designById(st.design);
    var f = S.fontOf(st.font);
    var same = function (a, b) { return String(a || '').toLowerCase() === String(b || '').toLowerCase(); };
    var cname = S.data.COLORS.filter(function (c) { return same(c.hex, st.bg); })[0];
    var tname = S.data.TEXT_COLORS.filter(function (c) { return same(c.hex, st.fg); })[0];
    // Exact colour for the order page: "Jää #66C7E9" when the colour has a name, otherwise just the code
    var code = function (list, hex) {
      var c = list.filter(function (x) { return same(x.hex, hex); })[0];
      var h = String(hex || '').toUpperCase();
      return c ? c.name + ' ' + h : h;
    };
    var pair = function (t) { return code(S.data.COLORS, t.bg) + ' / ' + code(S.data.TEXT_COLORS, t.fg); };
    var multi = !!this.palette() && !st.image;
    if (st.text2.trim()) props.push(['Toinen rivi', st.text2.trim()]);
    if (st.text3.trim()) props.push(['Kolmas rivi', st.text3.trim()]);
    if (this.spec.items) props.push(['Paketin sisältö', this.spec.items.map(function (i) { return (i.qty > 1 ? i.qty + ' × ' : '') + i.label; }).join(' + ')]);
    if (this.mode !== 'photo' && d) props.push(['Malli', st.custom ? tpl(NL.strings.designEdited || '{{ name }}', { name: d.n }) : d.n]);
    if (f) props.push(['Fontti', f.name]);
    if (st.art && !st.image && S.artIndex[st.art] && !(d && d.a === st.art)) props.push(['Koriste', S.artIndex[st.art][1]]);
    if (st.custom && !st.image) {
      props.push(['Tarran väri', cname ? cname.name : st.bg]);
      if (st.pattern) props.push(['Kuvio', (S.byId(S.data.PATTERNS, st.pattern.id) || {}).name || st.pattern.id]);
    }
    if ((st.custom || st.fgCustom || this.mode === 'photo') && tname) props.push(['Tekstin väri', tname.name]);
    if (st.upper) props.push(['Isot kirjaimet', 'Kyllä']);
    // The exact colours of every design go on the order line too, so an order can be laid out by hand straight from the
    // order page. The underscore hides the line from the customer; the admin shows it.
    var codes = multi ? 'tausta / teksti: ' + this.styles().map(pair).join('; ')
      : st.image ? 'teksti: ' + code(S.data.TEXT_COLORS, st.fg)
      : 'tausta / teksti: ' + pair(st);
    if (!multi && !st.image && this.groups().some(function (g) { return g.shape === 'mixed'; })) {
      codes += ' | muut tarrat: ' + [1, 2].map(function (k) { return pair(this.toneStyle(k)); }, this).join('; ');
    }
    props.push(['_Värikoodit', codes]);
    var html = props.map(function (p) {
      return '<input type="hidden" name="properties[' + esc(p[0]) + ']" value="' + esc(p[1]) + '">';
    }).join('');
    html += '<input type="hidden" name="properties[_design]" value="' + esc(JSON.stringify(this.designJson())) + '">';
    this.el.props.innerHTML = html;
  };

  Designer.prototype.showNameError = function (show) {
    var el = this.el.nameError;
    if (!el) return;
    el.hidden = !show;
    if (show) el.textContent = NL.strings.nameRequired || '';
    if (this.el.text) this.el.text.setAttribute('aria-invalid', show ? 'true' : 'false');
  };

  Designer.prototype.showAdded = function (show) {
    var box = $('[data-added]', this.root);
    if (box) box.hidden = !show;
  };

  Designer.prototype.showFormError = function (message) {
    var el = this.el.formError;
    if (!el) return;
    el.hidden = !message;
    el.textContent = message || '';
  };

  Designer.prototype.submit = function () {
    var self = this, st = this.state;
    this.showFormError('');
    if (!st.text.trim() && this.mode !== 'photo') {
      this.showNameError(true);
      if (this.el.text) { this.el.text.focus(); this.el.text.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      return;
    }
    if (this.mode === 'photo' && !st.image) {
      this.showFormError(NL.strings.imageMissing || 'Lataa ensin kuva tarraan.');
      return;
    }
    if (this.photoJob) {                       // the shrunk picture is still being prepared: send it once it is ready
      this.photoJob.then(function () { self.submit(); });
      return;
    }
    // Blocking mode of the fit guard: the font metrics must be here to decide (they usually are). If they cannot be loaded the guard
    // stays silent: it never stops an order because of its own failure.
    if (this.fitMode() === 'block') {
      var F = window.NLFit;
      F.loadFont(F.fontId(st.font)).then(function () { self.finishSubmit(); });
      return;
    }
    this.finishSubmit();
  };

  Designer.prototype.finishSubmit = function () {
    var self = this;
    if (this.fitMode() === 'block') {
      var fit = this.computeFit();
      if (fit.msgs.length) {
        this.updateFit();
        this.fitFormMsg = fit.msgs[0];
        this.showFormError(fit.msgs[0]);
        var field = [this.el.text, this.el.text2, this.el.text3][fit.first] || this.el.text;
        if (field && !field.closest('[hidden]')) { field.focus(); field.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        return;
      }
    }
    this.syncProps();
    var btn = this.el.submit;
    if (btn.disabled) return;
    btn.classList.add('is-loading');
    btn.disabled = true;

    var fd = new FormData(this.form);
    var file = fd.get('properties[Kuva]');
    if (file && typeof file === 'object' && file.size === 0) fd.delete('properties[Kuva]');

    var source = this.el.single;
    window.NLCart.add(fd)
      .then(function () {
        var fly = window.NLFlyToCart ? window.NLFlyToCart(source) : Promise.resolve();
        self.showAdded(true);
        return fly.then(function () { document.dispatchEvent(new CustomEvent('cart:add')); });
      })
      .catch(function (err) {
        self.showFormError(err && err.message ? err.message : NL.strings.error);
      })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
  };

  /* ---------- Photo upload -------------------------------------------------------------------------------------- */

  Designer.prototype.bindPhoto = function () {
    var self = this;
    var input = this.el.photoInput;
    if (!input) return;
    var st = this.state;
    var tools = this.el.photoTools;
    var zoom = $('[data-photo-zoom]', this.root);
    var px = $('[data-photo-x]', this.root);
    var py = $('[data-photo-y]', this.root);

    input.addEventListener('change', function () {
      var file = input.files && input.files[0];
      if (!file) { self.clearPhoto(); return; }
      if (file.size > MAX_FILE) {
        input.value = '';
        self.showFormError(NL.strings.imageTooBig);
        return;
      }
      self.showFormError('');
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        if (st.image && st.image.url) URL.revokeObjectURL(st.image.url);
        if (st.image && st.image.copy) URL.revokeObjectURL(st.image.copy.url);
        st.image = { url: url, nw: img.naturalWidth, nh: img.naturalHeight, zoom: 1, dx: 0, dy: 0 };
        self.makeSheetCopy(img, file, st.image);
        if (zoom) zoom.value = 1;
        if (px) px.value = 0;
        if (py) py.value = 0;
        if (tools) tools.hidden = false;
        if (!st.fgCustom) st.fg = '#FFFFFF';
        if (self.el.photoTitle) self.el.photoTitle.textContent = file.name;
        self.shrinkPhoto(file, img, input);
        self.syncPickers();
        self.schedulePaint();
      };
      img.src = url;
    });

    var upd = function () {
      if (!st.image) return;
      st.image.zoom = parseFloat(zoom.value);
      st.image.dx = parseFloat(px.value);
      st.image.dy = parseFloat(py.value);
      self.schedulePaint();
    };
    [zoom, px, py].forEach(function (el) { if (el) el.addEventListener('input', upd); });

    var rm = $('[data-photo-remove]', this.root);
    if (rm) rm.addEventListener('click', function () { input.value = ''; self.clearPhoto(); });
  };

  // Phone photos are 3-8 MB and 4000+ px, while a sticker shows a few hundred px of them. The browser shrinks the upload to
  // PHOTO_MAX_SIDE (JPEG; PNG when the file may have transparency) before it goes to the cart. The print files are resampled
  // to at most 300 ppi anyway. If anything here fails, or the result is not smaller, the original file is uploaded as it is.
  Designer.prototype.shrinkPhoto = function (file, img, input) {
    var self = this, mine = this.state.image;
    var w = img.naturalWidth, h = img.naturalHeight;
    var scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(w, h));
    if (scale === 1 && file.size <= PHOTO_SHRINK_ABOVE) return;
    if (!input || !window.DataTransfer || !window.File || !HTMLCanvasElement.prototype.toBlob) return;
    var alpha = file.type === 'image/png' || file.type === 'image/webp';
    var type = alpha ? 'image/png' : 'image/jpeg';
    var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
    var canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (!alpha) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cw, ch); }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, cw, ch);
    var job = new Promise(function (resolve) {
      canvas.toBlob(function (blob) {
        try {
          if (blob && blob.size < file.size && self.state.image === mine) {
            var dt = new DataTransfer();
            dt.items.add(new File([blob], file.name.replace(/\.[^.]*$/, '') + (alpha ? '.png' : '.jpg'), { type: type }));
            input.files = dt.files;
            mine.nw = cw; mine.nh = ch;
            self.schedulePaint();
          }
        } catch (e) { /* keep the original file */ }
        resolve();
      }, type, 0.9);
    });
    this.photoJob = job;
    job.then(function () { if (self.photoJob === job) self.photoJob = null; });
  };

  // The sheet preview draws the picture on every label (60 times on an A5 sheet of 18 mm rounds), and the original of a phone photo is
  // 4000+ px. So the sheet takes a small copy. It keeps the aspect ratio, so zoom and position (fractions of the picture) apply as they
  // are. Until the copy is ready (a few ms) the sheet shows the labels without the picture (Vate 20.10.2026: no picture on the sheet).
  Designer.prototype.makeSheetCopy = function (img, file, mine) {
    var self = this;
    var w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return;
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx || !canvas.toBlob) { mine.copy = { url: mine.url, nw: w, nh: h }; return; }
    var scale = Math.min(1, PHOTO_SHEET_SIDE / Math.max(w, h));
    var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
    var alpha = file.type === 'image/png' || file.type === 'image/webp';
    canvas.width = cw; canvas.height = ch;
    if (!alpha) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cw, ch); }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, cw, ch);
    canvas.toBlob(function (blob) {
      if (!blob || self.state.image !== mine) return;   // failed, or another picture (or none) by now: the sheet goes on without it
      mine.copy = { url: URL.createObjectURL(blob), nw: cw, nh: ch };
      self.schedulePaint();
    }, alpha ? 'image/png' : 'image/jpeg', 0.85);
  };

  // The picture as the sheet draws it: the small copy with the current zoom and position, or nothing while the copy is not ready.
  Designer.prototype.sheetImage = function () {
    var im = this.state.image;
    if (!im || !im.copy) return null;
    return { url: im.copy.url, nw: im.copy.nw, nh: im.copy.nh, zoom: im.zoom, dx: im.dx, dy: im.dy };
  };

  Designer.prototype.clearPhoto = function () {
    var st = this.state;
    this.photoJob = null;
    if (st.image && st.image.url) URL.revokeObjectURL(st.image.url);
    if (st.image && st.image.copy) URL.revokeObjectURL(st.image.copy.url);
    st.image = null;
    if (!st.fgCustom) st.fg = '#1B394E';
    if (this.el.photoTools) this.el.photoTools.hidden = true;
    if (this.el.photoTitle) this.el.photoTitle.textContent = 'Lataa kuva';
    this.syncPickers();
    this.schedulePaint();
  };

  // Drag the picture inside the sticker on the stage. The stage is re-rendered while dragging,
  // so the move/up listeners live on window instead of on the (replaced) element.
  Designer.prototype.bindStageDrag = function () {
    var self = this, st = this.state;
    var target = this.el.single && this.el.single.querySelector('.stage__sticker');
    if (!target) return;
    target.classList.add('is-draggable');
    target.addEventListener('pointerdown', function (e) {
      if (!st.image) return;
      e.preventDefault();
      var start = { x: e.clientX, y: e.clientY, dx: st.image.dx, dy: st.image.dy, w: target.getBoundingClientRect().width };
      var move = function (ev) {
        if (!st.image) return;
        var k = 2.4 / start.w;
        st.image.dx = Math.max(-1, Math.min(1, start.dx - (ev.clientX - start.x) * k));
        st.image.dy = Math.max(-1, Math.min(1, start.dy - (ev.clientY - start.y) * k));
        var px = $('[data-photo-x]', self.root), py = $('[data-photo-y]', self.root);
        if (px) px.value = st.image.dx;
        if (py) py.value = st.image.dy;
        self.schedulePaint();
      };
      var up = function () {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    });
  };

  /* ---------- Boot ------------------------------------------------------------------------------------------------ */

  function boot(scope) {
    $$('[data-product-section]', scope || document).forEach(function (el) {
      if (el.dataset.designerReady) return;
      el.dataset.designerReady = '1';
      el.designer = new Designer(el);
    });
  }

  function start() {
    if (!window.OmaSticker) return;
    boot(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  window.NLDesigner = { Designer: Designer, parseDims: parseDims, resolveSpec: resolveSpec };
})();
